import {
  BellOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  DashboardOutlined,
  FieldTimeOutlined,
  FileDoneOutlined,
  ProfileOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import React from 'react';

export type WorkbenchQueueType =
  | 'messages'
  | 'approvals'
  | 'task-confirmations'
  | 'schedule-confirmations'
  | 'expense-allocations';

export type WorkbenchActionQueueType = Exclude<WorkbenchQueueType, 'messages'>;

export interface WorkbenchQueueMeta {
  type: WorkbenchActionQueueType;
  path: string;
  label: string;
  permissions: readonly string[];
}

export interface WorkbenchNavigationItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  queueType?: WorkbenchQueueType;
  children?: WorkbenchNavigationItem[];
}

export const WORKBENCH_HOME_PATH = '/dashboard';
export const WORKBENCH_FIRST_TODO_PATH = '/dashboard/approvals';

export const WORKBENCH_ACTION_QUEUE_META: Record<WorkbenchActionQueueType, WorkbenchQueueMeta> = {
  approvals: {
    type: 'approvals',
    path: '/dashboard/approvals',
    label: '审批待办',
    permissions: ['workflow.flow.list', 'workflow.flow.get', 'workflow.flow.approve'],
  },
  'task-confirmations': {
    type: 'task-confirmations',
    path: '/dashboard/task-confirmations',
    label: '任务结果确认',
    permissions: ['crm.task.list'],
  },
  'schedule-confirmations': {
    type: 'schedule-confirmations',
    path: '/dashboard/schedule-confirmations',
    label: '日程结果确认',
    permissions: ['crm.schedule.list'],
  },
  'expense-allocations': {
    type: 'expense-allocations',
    path: '/dashboard/expense-allocations',
    label: '费用分摊确认',
    permissions: ['fin.expense.list'],
  },
};

const queueNavigationIcons: Record<WorkbenchActionQueueType, React.ReactNode> = {
  approvals: React.createElement(FileDoneOutlined),
  'task-confirmations': React.createElement(CheckCircleOutlined),
  'schedule-confirmations': React.createElement(FieldTimeOutlined),
  'expense-allocations': React.createElement(ProfileOutlined),
};

const actionQueueOrder: WorkbenchActionQueueType[] = [
  'approvals',
  'task-confirmations',
  'schedule-confirmations',
  'expense-allocations',
];

export const WORKBENCH_NAVIGATION_ITEMS: WorkbenchNavigationItem[] = [
  { key: WORKBENCH_HOME_PATH, icon: React.createElement(DashboardOutlined), label: '工作台首页' },
  { key: '/schedule', icon: React.createElement(CalendarOutlined), label: '日程管理' },
  { key: '/dashboard/messages', icon: React.createElement(BellOutlined), label: '我的消息', queueType: 'messages' },
  {
    key: 'workbench-todos',
    icon: React.createElement(UnorderedListOutlined),
    label: '我的待办',
    children: actionQueueOrder.map((type) => ({
      key: WORKBENCH_ACTION_QUEUE_META[type].path,
      icon: queueNavigationIcons[type],
      label: WORKBENCH_ACTION_QUEUE_META[type].label,
      queueType: type,
    })),
  },
];

export function getWorkbenchQueueMeta(type: WorkbenchActionQueueType): WorkbenchQueueMeta {
  return WORKBENCH_ACTION_QUEUE_META[type];
}

export function getWorkbenchQueueType(pathname: string): WorkbenchQueueType | null {
  const walk = (items: WorkbenchNavigationItem[]): WorkbenchQueueType | null => {
    for (const item of items) {
      if (item.key === pathname) return item.queueType ?? null;
      if (item.children?.length) {
        const childType = walk(item.children);
        if (childType) return childType;
      }
    }
    return null;
  };
  return walk(WORKBENCH_NAVIGATION_ITEMS);
}

export function isWorkbenchPath(pathname: string): boolean {
  return pathname === WORKBENCH_HOME_PATH || pathname.startsWith(`${WORKBENCH_HOME_PATH}/`);
}
